import { useParams } from 'react-router-dom';
import React, { useState, createContext, useContext, useEffect, useCallback } from 'react';

import { ICourse } from 'data/entities/course.entity';
import { IParams } from 'presentation/entities/params.entity';
import { redirectTo } from 'data/usecases/redirect-to';
import { environment } from 'utils/environment.utils';
import { completeLesson } from 'data/usecases/complete-lesson';
import { getCourseByIdOrSlug } from 'data/usecases/get-course-by-id-or-slug';
import { syncCourses } from 'data/usecases/sync-courses';

interface ICourseContext {
    course: ICourse | null;
    isLocked: boolean;
    isLoading: boolean;
    handleCompleteLesson: (moduleIndex: number, lessonIndex: number) => void;
}

interface IProps {
    children: React.ReactNode;
}

export const CourseContext = createContext<ICourseContext | null>(null);

export const CourseProvider = ({ children }: IProps): JSX.Element => {

    const { idOrSlug } = useParams<IParams>();
    const [course, setCourse] = useState<ICourse | null>(null);
    const [isLocked, setIsLocked] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        (async () => {
            if (!course) {
                const courseByIdOrSlug = await getCourseByIdOrSlug(idOrSlug);
                if (!courseByIdOrSlug) return redirectTo(environment.URLS.APP_COURSES);
                syncCourses(idOrSlug);

                setCourse(courseByIdOrSlug);
                setIsLoading(false);
            }

        })();
    }, [course, idOrSlug]);

    useEffect(() => { if (course?.isFree || (!course?.isFree && course?.isAllowedToCustomer)) { setIsLocked(false); } }, [course]);

    const handleCompleteLesson = useCallback(
        (moduleIndex, lessonIndex): void => completeLesson({ course, moduleIndex, lessonIndex, setCourse }),
        [course],
    );

    return (
        <CourseContext.Provider value={{ isLoading, course, isLocked, handleCompleteLesson }}>
            {children}
        </CourseContext.Provider>
    );
};

export const useCourse = () => {

    const context = useContext(CourseContext);
    if (!context) throw new Error('useCourse must be used within an CourseProvider');

    return context;

};
